import type { JSX } from 'react';
import { Skeleton } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';

import { Panel, Tag } from '../ds';
import type { MyPlanCardDto } from '../../lib/api/openingTasks';
import { useOpeningTasks } from '../opening-tasks/OpeningTasksSection';
import { RoomsPanel } from '../opening-tasks/RoomsPanel';
import { fmtDate } from '../opening-tasks/utils';
import { appealSubmissions, PLAN_COPY, submissionStatus, type PlanMode } from './plan-model';

/** «Мои помещения» с перепиской — только у партнёра (помещения приходят из его «Моих задач»). */
function PartnerRooms(): JSX.Element {
  const opening = useOpeningTasks();
  return opening.pending ? (
    <Skeleton active paragraph={{ rows: 3 }} />
  ) : (
    // Не загрузились «Мои задачи» — помещений не видно, но «Отправить новое помещение» остаётся.
    <RoomsPanel rooms={opening.query.data?.rooms ?? []} />
  );
}

/**
 * Вкладка «Обращения»: «Мои помещения» (заявки «Осмотр помещения» с перепиской куратора и моментами; окно помещения
 * открывается по `?room=<id>`, вкладка в адресе остаётся) и «Заявки по открытию» — остальные заявки по этой точке:
 * вид, статус, стадия, дата подачи. Ссылок в CRM нет: партнёр видит только сведения.
 * У менеджера (`mode="manager"`) помещений с перепиской нет (она в CRM, раздел «Обращения»), а в заявках — все,
 * включая осмотры помещений.
 */
export function PlanAppealsTab({ card, mode = 'partner' }: { card: MyPlanCardDto; mode?: PlanMode }): JSX.Element {
  const manager = mode === 'manager';
  const subs = appealSubmissions(card.submissions, mode);
  return (
    <div className="fk-op-panel">
      {manager ? null : <PartnerRooms />}

      <Panel pad={0} icon={<FileTextOutlined />} title="Заявки по открытию" sub={PLAN_COPY[mode].appealsSub}>
        {subs.length === 0 ? (
          <div className="fk-ot-empty">{manager ? 'Заявок по открытию пока нет.' : 'Других заявок по открытию пока нет.'}</div>
        ) : (
          subs.map((s) => {
            const st = submissionStatus(s.status);
            return (
              <div key={s.id} className="fk-op-sub">
                <div className="fk-op-sub-t">{s.typeLabel}</div>
                <div className="fk-op-sub-m">
                  <Tag color={st.color}>{st.label}</Tag>
                  {s.stageLabel ? <span>{s.stageLabel}</span> : null}
                  <span>подана {fmtDate(s.createdAt)}</span>
                </div>
              </div>
            );
          })
        )}
      </Panel>

      {manager ? <div className="fk-ot-note">Переписка по помещениям — в CRM, раздел «Обращения».</div> : null}
    </div>
  );
}
